// The product page and each grid section load this file, so a page with two of
// them loads it twice. The second copy does nothing instead of throwing
// "already declared". The body isn't re-indented, to keep history.
(() => {
if (window.__voraxCartPreviewLoaded) return;
window.__voraxCartPreviewLoaded = true;

// On failure the pop-up shows the message over a freshly fetched cart, because
// a 422 can still add part of the quantity. The error is re-thrown so the
// caller can reset its button.
async function addVariantToCart(variantId, quantity = 1) {
  try {
    const added = await VoraxCart.request(
      "cart/add.js",
      VoraxCart.withSections({ id: variantId, quantity }),
    );

    const cart = await VoraxCart.getCart();

    window.syncCart?.(cart, added.sections);

    window.cartPreviewModal.open(cart);

    return cart;
  } catch (error) {
    const cart = await VoraxCart.getCart().catch(() => null);

    window.syncCart?.(cart);

    window.cartPreviewModal.open(cart, error.message);

    throw error;
  }
}

class CartPreviewModal {
  constructor(element) {
    this.element = element;

    this.state = {
      cart: null,
    };

    // One request at a time; every change re-renders from the cart it returns.
    this.busy = false;

    this.version = 0;

    this.elements = this.cacheElements();

    this.bindEvents();
  }

  cacheElements() {
    return {
      overlay: this.element.querySelector("#cartModalOverlay"),

      close: this.element.querySelector("#closeCartModal"),

      items: this.element.querySelector("#modalCartItems"),

      summary: this.element.querySelector("#modalCartSummary"),

      count: this.element.querySelector("#modalCartCount"),

      checkout: this.element.querySelector("#modalCheckout"),

      continueShopping: this.element.querySelector("#continueShopping"),

      error: this.element.querySelector("#modalCartError"),
    };
  }

  bindEvents() {
    this.elements.overlay.addEventListener("click", this.close.bind(this));

    this.elements.close.addEventListener("click", this.close.bind(this));

    this.elements.continueShopping.addEventListener(
      "click",
      this.close.bind(this),
    );

    this.elements.checkout.addEventListener("click", this.checkout.bind(this));

    this.elements.items.addEventListener(
      "click",
      this.handleItemsClick.bind(this),
    );
  }

  handleItemsClick(event) {
    const button = event.target.closest(".cart-qty-btn");

    if (!button || this.busy) return;

    const item = this.state.cart?.items.find(
      (item) => item.key === button.dataset.key,
    );

    if (!item) return;

    const quantity =
      button.dataset.action === "increase"
        ? item.quantity + 1
        : item.quantity - 1;

    this.updateQuantity(item.key, quantity);
  }


  async updateQuantity(key, quantity) {
    if (quantity < 0 || this.busy) return;

    this.busy = true;

    // open() bumps this, so a slow change never overwrites a newer cart.
    const version = this.version;

    this.showError();
    this.setLoading(true);

    try {
      const cart = await VoraxCart.request(
        "cart/change.js",
        VoraxCart.withSections({ id: key, quantity }),
      );

      if (version !== this.version) {
        await this.refresh();
        return;
      }

      this.state.cart = cart;

      window.syncCart?.(cart, cart.sections);

      const line = cart.items.find((item) => item.key === key);

      if (line && line.quantity < quantity) {
        this.showError(VoraxCart.quantityError(line.quantity));
      }
    } catch (error) {
      this.showError(error.message);

      await this.refresh();
    } finally {
      this.busy = false;

      this.render();

      this.setLoading(false);
    }
  }

  async refresh() {
    const cart = await VoraxCart.getCart().catch(() => null);

    if (!cart) return;

    this.state.cart = cart;

    window.syncCart?.(cart);
  }


  setLoading(loading) {
    this.elements.items
      .querySelectorAll(".cart-qty-btn, .cart-remove-btn")
      .forEach((button) => {
        button.disabled = loading;

        button.classList.toggle("opacity-50", loading);

        button.classList.toggle("pointer-events-none", loading);
      });

    // Checkout also stays off while the bag is empty.
    const disabled = loading || !this.state.cart?.item_count;

    this.elements.checkout.disabled = disabled;

    this.elements.checkout.classList.toggle("opacity-50", disabled);

    this.elements.checkout.classList.toggle("pointer-events-none", disabled);
  }

  // No message hides the alert.
  showError(message) {
    this.elements.error.textContent = message || "";

    this.elements.error.classList.toggle("hidden", !message);
  }

  open(cart, message) {
    if (cart) this.state.cart = cart;

    this.version += 1;

    this.showError(message);

    this.render();

    this.setLoading(this.busy);

    this.element.classList.remove("hidden");
  }

  close() {
    this.element.classList.add("hidden");

    this.showError();

    this.state.cart = null;
  }

  checkout() {
    if (this.busy || !this.state.cart?.item_count) return;

    window.location.href = VoraxCart.url("checkout");
  }

  render() {
    // No cart could be fetched: don't leave a previous bag on screen.
    if (!this.state.cart) {
      this.elements.items.innerHTML = "";
      this.elements.summary.innerHTML = "";
      return;
    }

    this.renderItems();

    this.renderSummary();

    this.renderCount();
  }

  renderCount() {
    this.elements.count.textContent = `(${this.state.cart.item_count})`;
  }
  renderOptions(item) {
    return item.options_with_values
      .map(
        (option) => `
        <span class="text-[13px]">
          ${option.name}: ${option.value}
        </span>
      `,
      )
      .join("");
  }
  renderItem(item) {
    return `
    <article
      class="flex gap-6 p-6 border-b"
      data-key="${item.key}"
    >

      ${item.image ? `<img
        src="${VoraxCart.imageUrl(item.image, 200)}"
        alt="${item.product_title}"
        class="w-[100px] h-[100px] object-cover rounded-lg"
      >` : ""}

      <div class="flex-1">

        <h4 class="font-inter-bold!">
          ${item.product_title}
        </h4>

        <div class="flex flex-col text-sm mt-1 font-inter-light">

          ${this.renderOptions(item)}

        </div>

        <div class="mt-3 flex justify-between items-center ">

          <span class="font-bebasneue text-4xl">

            ${VoraxCart.formatMoney(item.final_line_price)}

          </span>

          <div class="cart-quantity flex items-center mt-4 gap-2 bg-primary-cream w-fit shadow-qty-button rounded-lg font-inter-bold px-4 gap-4">

              <button
                  type="button"
                  class="cart-qty-btn cursor-pointer"
                  data-action="decrease"
                  data-key="${item.key}">

                  -

              </button>

              <span>

                  ${item.quantity}

              </span>

              <button
                  type="button"
                  class="cart-qty-btn cursor-pointer"
                  data-action="increase"
                  data-key="${item.key}">

                  +

              </button>

          </div>

        </div>

      </div>

    </article>
  `;
  }
  renderItems() {
    this.elements.items.innerHTML = this.state.cart.items
      .map((item) => this.renderItem(item))
      .join("");
  }
  renderSummary() {
    const cart = this.state.cart;

    const hasDiscount = cart.original_total_price > cart.total_price;

    let html = "";

    html += `
        <div class="flex justify-between font-inter-light">

            <span>${VoraxCart.label("subtotal")}</span>

            <span>${VoraxCart.formatMoney(cart.original_total_price)}</span>

        </div>
    `;

    if (hasDiscount) {
      const discount = cart.original_total_price - cart.total_price;

      html += `
            <div class="flex justify-between font-inter-light">

                <span>

                    ${VoraxCart.label("discount")}
                    ${
                      cart.cart_level_discount_applications.length
                        ? `(${cart.cart_level_discount_applications
                            .map((d) => d.title)
                            .join(", ")})`
                        : ""
                    }

                </span>

                <span>

                    -${VoraxCart.formatMoney(discount)}

                </span>

            </div>
        `;
    }

    html += `
        <div class="border-t pt-3 mt-3 flex justify-between font-bebasneue text-4xl">

            <span>${VoraxCart.label("total")}</span>

            <span>${VoraxCart.formatMoney(cart.total_price, true)}</span>

        </div>
    `;

    this.elements.summary.innerHTML = html;
  }
}

window.cartPreviewModal = new CartPreviewModal(
  document.querySelector("#cartModal"),
);


window.addVariantToCart = addVariantToCart;
})();
